import { useState } from 'react'
import { message } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'

/**
 * Deletes a node. A team is removed from its competition; a schedule node is
 * deleted outright. Invalidates on settle so a failed delete refetches the true
 * server state rather than leaving the row missing from the tree.
 */
export const useDeleteNode = (rootScheduleId: string) => {
  'use memo'

  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)

  const mutation = useMutation({
    mutationFn: async (node: OperationsNode) => {
      const url = isTeamNode(node)
        ? `/schedules/${node.scheduleId}/teams/${node.teamId}`
        : `/schedules/${node.scheduleId}`
      await req(url, { method: 'DELETE' })
    },
    onMutate: () => setIsDeleting(true),
    onError: (error: Error) => {
      message.error(getErrorMessage(error))
    },
    onSettled: () => {
      setIsDeleting(false)
      queryClient.invalidateQueries({
        queryKey: queryKeys.operations.tree(rootScheduleId),
      })
    },
  })

  return {
    deleteNode: (node: OperationsNode) => mutation.mutateAsync(node),
    isDeleting,
  }
}
